import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { CreditCard, ChevronRight, Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { PageHeader } from '@/components/ui/page-header'
import { EmptyState } from '@/components/ui/empty-state'
import { TableSkeleton } from '@/components/ui/skeleton'
import { SortHead, useTableSort, cmp } from '@/components/ui/sortable-head'
import { useAccounts, useCardTotalDebt, useCardLandingMonth } from '@/hooks/useAccounts'
import { useCurrentRates, convertWithRates } from '@/hooks/useCurrentRates'
import { useHousehold } from '@/context/HouseholdContext'
import { useT } from '@/i18n/I18nProvider'
import { formatCurrency } from '@/lib/currency'
import { dueDateForMonth, dueISOForMonth, suggestStatementMonth } from '@/lib/cards'
import { accountIcon } from '@/lib/accountIcon'
import { AccountFormDialog } from '@/components/accounts/AccountFormDialog'
import type { Account } from '@/types/db'

export function CardsPage() {
  const { t } = useT()
  const { baseCurrency } = useHousehold()
  const navigate = useNavigate()
  const [dialogOpen, setDialogOpen] = useState(false)
  const { data: accounts, isLoading } = useAccounts()
  // Coluna "fatura" = o que é COBRADO no mês (régua byStatement), igual à tela do
  // cartão. A coluna "total" ao lado continua sendo a dívida real (todas as datas).
  const { data: cardTotal } = useCardTotalDebt()
  const { data: rates } = useCurrentRates()
  // Data de hoje (local) — usada para achar a FATURA EM ABERTO de cada cartão.
  const todayISO = useMemo(() => {
    const n = new Date()
    const p = (x: number) => String(x).padStart(2, '0')
    return `${n.getFullYear()}-${p(n.getMonth() + 1)}-${p(n.getDate())}`
  }, [])
  // Fatura de destino ao clicar E exibida na coluna: fatura FECHADA não paga (se
  // houver) ou a EM ABERTO. O hook decide e traz o valor.
  const { data: landingMonth } = useCardLandingMonth()
  const openFaturaMonth = (c: { id: string; closing_day: number | null; due_day: number | null }) =>
    landingMonth?.get(c.id)?.month ?? suggestStatementMonth(todayISO, c.closing_day, c.due_day).slice(0, 7)

  const cards = useMemo(() => (accounts ?? []).filter((a) => a.type === 'cartao'), [accounts])

  // Vencimento é o da fatura EXIBIDA (aberta ou fechada não paga) — não o mês
  // calendário atual, que pode já ter virado (fatura aberta de setembro com hoje
  // ainda em agosto, ou fatura fechada de um mês anterior ainda não paga).
  const dueDateOf = (c: Account) => {
    const [y, m] = openFaturaMonth(c).split('-').map(Number)
    return dueDateForMonth(c.due_day, y, m - 1)
  }
  const dueISOOf = (c: Account) => {
    const [y, m] = openFaturaMonth(c).split('-').map(Number)
    return dueISOForMonth(c.due_day, y, m - 1)
  }

  // Fatura a exibir (fechada não paga ou aberta) — valor na moeda do cartão.
  const faturaNative = (c: Account) => landingMonth?.get(c.id)?.debt ?? 0
  // Dívida TOTAL do cartão (todos os meses/status não pagos) — saldo all-status (neg=dívida).
  const totalNative = (c: Account) => Math.max(-(cardTotal?.get(c.id) ?? 0), 0)
  const totalBase = (c: Account) => convertWithRates(totalNative(c), c.currency, rates) ?? 0

  type CKey = 'card' | 'due' | 'invoice' | 'total' | 'base'
  const { sort, toggle } = useTableSort<CKey>('total', 'desc', ['invoice', 'total', 'base'])
  const sortedCards = useMemo(() => {
    const val = (c: Account): string | number => {
      switch (sort.key) {
        case 'card':
          return c.name
        case 'due':
          return dueISOOf(c) ?? ''
        case 'invoice':
          return faturaNative(c)
        case 'total':
          return totalNative(c)
        case 'base':
          return totalBase(c)
      }
    }
    return cards.slice().sort((a, b) => cmp(val(a), val(b), sort.dir))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cards, landingMonth, cardTotal, rates, sort])

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <PageHeader
        title={t('nav.cards')}
        subtitle={t('acct.cardsSubtitle')}
        actions={
          <Button onClick={() => setDialogOpen(true)}>
            <Plus className="size-4" /> {t('acct.newCard')}
          </Button>
        }
      />

      <Card>
        <CardContent className="p-0">
          {isLoading || accounts === undefined ? (
            <TableSkeleton rows={6} cols={4} />
          ) : cards.length === 0 ? (
            <EmptyState
              icon={CreditCard}
              title={t('acct.cardsEmptyTitle')}
              description={t('acct.cardsEmptyDesc')}
              action={
                <Button onClick={() => setDialogOpen(true)}>
                  <Plus className="size-4" /> {t('acct.newCard')}
                </Button>
              }
            />
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <SortHead label={t('acct.colCard')} k="card" sort={sort} onToggle={toggle} />
                    <SortHead label={t('acct.colDueDate')} k="due" sort={sort} onToggle={toggle} />
                    <SortHead label={t('acct.colMonthInvoice')} k="invoice" sort={sort} onToggle={toggle} className="text-right" />
                    <SortHead label={t('acct.colTotalDebt')} k="total" sort={sort} onToggle={toggle} className="text-right" />
                    <SortHead label={t('acct.colTotalIn', { currency: baseCurrency })} k="base" sort={sort} onToggle={toggle} className="text-right" />
                    <TableHead className="w-10" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedCards.map((c) => {
                    const nat = faturaNative(c)
                    const tot = totalNative(c)
                    const totBas = totalBase(c)
                    const due = dueDateOf(c)
                    return (
                      <TableRow
                        key={c.id}
                        className="hover:bg-accent/40 cursor-pointer"
                        onClick={() => navigate(`/cartoes/${c.id}?m=${openFaturaMonth(c)}`)}
                      >
                        <TableCell className="font-medium">
                          <span className="inline-flex items-center gap-1.5">
                            <span className="text-base leading-none">{accountIcon(c)}</span> {c.name}
                          </span>
                        </TableCell>
                        <TableCell className="whitespace-nowrap tabular-nums">
                          {due ?? <span className="text-muted-foreground">—</span>}
                        </TableCell>
                        <TableCell className="text-right tabular-nums whitespace-nowrap">
                          {nat > 0.005 ? (
                            <span className="inline-flex flex-col items-end leading-tight">
                              <span className={landingMonth?.get(c.id)?.closed ? 'text-destructive' : 'text-amber-600'}>
                                {formatCurrency(nat, c.currency)}
                              </span>
                              <span className="text-muted-foreground text-[10px]">
                                {landingMonth?.get(c.id)?.closed ? t('acct.invoiceClosedUnpaid') : t('acct.invoiceOpen')}
                              </span>
                            </span>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right tabular-nums whitespace-nowrap">
                          {tot > 0.005 ? (
                            <span className="text-destructive font-medium">{formatCurrency(tot, c.currency)}</span>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right tabular-nums whitespace-nowrap">
                          {c.currency !== baseCurrency && tot > 0.005 ? (
                            formatCurrency(totBas, baseCurrency)
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <ChevronRight className="text-muted-foreground ml-auto size-4" />
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <AccountFormDialog open={dialogOpen} onOpenChange={setDialogOpen} defaultType="cartao" />
    </div>
  )
}
